import axios from "axios";
import { store } from "./store";
import { ROOT } from "./redux/constants";
import config from "./config/config.json";

let baseUrl = config.PROTOCOL + config.SERVER + ":" + config.PORT;

const instance = axios.create({
  baseURL: baseUrl + "/" + config.API_VERSION,
});

// SINGLE-FLIGHT refresh promise (prevents parallel /auth/refresh calls)
let refreshPromise = null;

instance.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const accessToken = state.root?.accessToken;

    // Some requests (e.g. /auth/refresh) should not include the accessToken
    if (!config.skipAuth && accessToken) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = "Bearer " + accessToken;
    }

    config.headers = config.headers || {};
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    config.timeout = 10000;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    try {
      const originalRequest = error?.config;

      // If we somehow have no original request, just reject
      if (!originalRequest) {
        return Promise.reject(error);
      }

      const status = error?.response?.status;
      const skipLogout = originalRequest?.skipLogout;

      // Caller opted out of global handling
      if (skipLogout) {
        return Promise.reject(error);
      }

      // If the /auth/refresh call itself fails, do NOT try to refresh again
      if (originalRequest.url?.includes("/auth/refresh")) {
        store.dispatch({ type: ROOT.LOGOUT });
        return Promise.reject(error);
      }

      // 401: try one refresh, then logout if it still fails
      if (status === 401) {
        if (!originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const state = store.getState();
            const refreshToken = state.root?.refreshToken;

            // If no refresh token, no point refreshing
            if (!refreshToken) {
              store.dispatch({ type: ROOT.LOGOUT });
              return Promise.reject(error);
            }

            // SINGLE-FLIGHT refresh: only one refresh request in-flight for all 401s
            if (!refreshPromise) {
              refreshPromise = (async () => {
                const refreshResponse = await instance.post(
                  baseUrl + "/auth/refresh",
                  {
                    ...(refreshToken ? { refreshToken: refreshToken } : {}),
                  },
                  {
                    // avoid global logout handling inside this refresh call
                    skipLogout: true,
                    // do not attach (possibly expired) accessToken on refresh call
                    skipAuth: true,
                  },
                );

                return refreshResponse.data;
              })().finally(() => {
                refreshPromise = null;
              });
            }

            const data = await refreshPromise;

            // Support both raw string or { accessToken/access_token, refreshToken/refresh_token }
            const accessToken =
              typeof data === "string"
                ? data
                : (data?.accessToken ?? data?.access_token);

            const nextRefreshToken =
              typeof data === "object" && data
                ? (data?.refreshToken ?? data?.refresh_token)
                : undefined;

            if (!accessToken) {
              store.dispatch({ type: ROOT.LOGOUT });
              return Promise.reject(error);
            }

            // Update store
            store.dispatch({
              type: ROOT.UPDATE_TOKEN,
              payload: {
                accessToken,
                ...(nextRefreshToken ? { refreshToken: nextRefreshToken } : {}),
              },
            });

            // Update default headers
            instance.defaults.headers.common["Authorization"] =
              "Bearer " + accessToken;

            // Update this specific request
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers["Authorization"] = "Bearer " + accessToken;

            // Retry using the same axios instance (with interceptors)
            return instance(originalRequest);
          } catch (refreshErr) {
            // Refresh failed → logout once
            store.dispatch({ type: ROOT.LOGOUT });
            return Promise.reject(refreshErr);
          }
        }

        // Already retried once → logout and reject
        store.dispatch({ type: ROOT.LOGOUT });
        return Promise.reject(error);
      }

      // 403: user is authenticated but not allowed → do NOT logout, just reject
      if (status === 403) {
        return Promise.reject(error);
      }

      // Other statuses: just reject
      return Promise.reject(error);
    } catch (err) {
      console.error("axios interceptor error:", err);
      return Promise.reject(error);
    }
  },
);

export { instance, baseUrl };
